import { Alert, Text } from '@chakra-ui/react';
import { Check } from '@time-line/components/icons';
import type { ColorScheme } from 'components/heros/PromotionHero';
import { useState } from 'react';

import { SubscribeEmail } from './SubscribeEmail';
import { SubscribePhoneNumber } from './SubscribePhoneNumber';

// SHOP-8528: Add klaviyoListId to subscribe doc
// useSubscribeSignup hook will be replaced with useListSignup once the subscribe doc has a klaviyoListId.
// See various related SHOP-8528 comments in the the codebase.

interface SubscribeWizardProps {
  consentType: 'email' | 'sms' | 'both';
  buttonColorScheme: ColorScheme;
  colorScheme: ColorScheme;
  klaviyoListId?: string; // SHOP-8528: will be mandatory
  ctaTitle?: string;
  autoSubscribeMarketing?: boolean | null;
}

export function SubscribeWizard({
  buttonColorScheme,
  consentType,
  colorScheme,
  klaviyoListId,
  ctaTitle,
  autoSubscribeMarketing,
}: SubscribeWizardProps) {
  const [email, setEmail] = useState<string>('');
  const [emailConsent, setEmailConsent] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const handleSuccess = (value: string, consented: boolean) => {
    setCurrentStep((prevStep) => prevStep + 1);
    setEmail(value);
    setEmailConsent(consented);
  };

  const colorSchemes = {
    colorScheme,
    buttonColorScheme,
  };

  return (
    <>
      {consentType === 'email' && (
        <SubscribeEmail
          klaviyoListId={klaviyoListId}
          ctaTitle={ctaTitle}
          autoSubscribeMarketing={autoSubscribeMarketing}
          {...colorSchemes}
        />
      )}

      {consentType === 'sms' && (
        <SubscribePhoneNumber
          colorScheme={colorScheme}
          buttonColorScheme={buttonColorScheme}
          klaviyoListId={klaviyoListId}
          autoSubscribeMarketing={autoSubscribeMarketing}
        />
      )}

      {consentType === 'both' && (
        <>
          {currentStep === 0 && (
            <SubscribeEmail
              {...colorSchemes}
              handleSuccess={handleSuccess}
              klaviyoListId={klaviyoListId}
              ctaTitle={ctaTitle}
              autoSubscribeMarketing={autoSubscribeMarketing}
            />
          )}
          {currentStep === 1 && (
            <>
              <Alert
                status="success"
                borderRadius="md"
                p="2xs"
                pr="sm"
                gap="3xs"
                mb={4}
                bgColor={
                  colorScheme === 'white' ? 'whiteAlpha.200' : 'blackAlpha.50'
                }
                color={colorScheme === 'white' ? 'gray.50' : 'gray.600'}
              >
                <Check />
                <Text size="detail">Thanks! Your email has been saved.</Text>
              </Alert>
              <SubscribePhoneNumber
                email={email}
                emailConsent={emailConsent}
                klaviyoListId={klaviyoListId}
                autoSubscribeMarketing={autoSubscribeMarketing}
                {...colorSchemes}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
